import { FC, useState } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../common/components/Button'
import { AuthTokenFactory } from '../../../auth/AuthTokenFactory'
import { ApiShippingAddress } from '../../../api/quotes/types/ApiShippingAddress'
import { CreateQuoteAddressSelect } from './CreateQuoteAddressSelect'
import { CreateQuoteFormData } from '../model/types'
import { MoneyView } from '../../../common/components/MoneyView'
import { animations } from '../../../common/animations'

const Form = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing.md,
}))

const Field = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing.xs,
}))

const Label = styled('label')(({ theme }) => ({
  fontSize: theme.fontSize.small,
  color: theme.colors.text.primary,
}))

const Input = styled('input')(({ theme }) => ({
  padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: 4,
  fontSize: theme.fontSize.regular,
  color: theme.colors.text.primary,
  outline: 'none',
  '&:focus': {
    borderColor: theme.colors.primary.main,
  },
}))

const ShippingCostInput = styled(Input)<{ highlighted?: boolean }>(({ theme, highlighted }) =>
  highlighted
    ? {
        borderColor: theme.colors.primary.main,
        animation: `${animations.pulseGlow} 2s ease-in-out infinite`,
      }
    : {},
)

const CostDisplay = styled('div')(({ theme }) => ({
  padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
  backgroundColor: theme.colors.background.muted,
  borderRadius: 4,
  fontSize: theme.fontSize.regular,
  color: theme.colors.text.primary,
}))

interface CreateQuoteFormStepProps {
  addresses: ApiShippingAddress[]
  formData: CreateQuoteFormData
  onFormDataChange: (data: CreateQuoteFormData) => void
  onNext: () => void
}

export const CreateQuoteFormStep: FC<CreateQuoteFormStepProps> = ({
  addresses,
  formData,
  onFormDataChange,
  onNext,
}) => {
  const { t } = useTranslation()
  const isAgent = AuthTokenFactory.isAgent()
  const [costHighlighted, setCostHighlighted] = useState(false)

  const selectedAddress = addresses.find((a) => a.id === formData.shippingAddressId)

  const handleAddressChange = (addressId: string) => {
    if (addressId === formData.shippingAddressId) return

    const address = addresses.find((a) => a.id === addressId)
    const customShippingCost = address?.price.value ?? 0

    onFormDataChange({
      ...formData,
      shippingAddressId: addressId,
      customShippingCost,
    })
    setCostHighlighted(customShippingCost !== formData.customShippingCost)
  }

  return (
    <Form>
      <Field>
        <Label>{t('Reference number')}</Label>
        <Input
          type="text"
          placeholder={t('Enter reference number (optional)')}
          value={formData.referenceNumber}
          onChange={(e) => onFormDataChange({ ...formData, referenceNumber: e.target.value })}
        />
      </Field>

      <Field>
        <Label>{t('Shipping address')}</Label>
        <CreateQuoteAddressSelect
          addresses={addresses}
          selectedAddressId={formData.shippingAddressId}
          onChange={handleAddressChange}
        />
      </Field>

      <Field>
        <Label>{t('Shipping cost')}</Label>
        {isAgent ? (
          <ShippingCostInput
            highlighted={costHighlighted}
            type="number"
            min={0}
            step="0.01"
            value={formData.customShippingCost}
            onChange={(e) => {
              onFormDataChange({ ...formData, customShippingCost: e.target.valueAsNumber })
              setCostHighlighted(false)
            }}
            onFocus={() => setCostHighlighted(false)}
            onBlur={(e) => {
              if (isNaN(e.target.valueAsNumber)) {
                onFormDataChange({ ...formData, customShippingCost: 0 })
              }
            }}
          />
        ) : (
          <CostDisplay>
            <MoneyView value={{ value: formData.customShippingCost, currency: selectedAddress?.price.currency ?? '' }} />
          </CostDisplay>
        )}
      </Field>

      <Button primary big onClick={onNext} disabled={!formData.shippingAddressId}>
        {t('Next')}
      </Button>
    </Form>
  )
}
